<template>
    <Teleport v-if="printing" to="body">
        <div id="message-hub-print-view" class="bg-white text-slate-800 p-8 text-sm">
            <div class="mb-6 border-b border-solid border-slate-200 pb-4">
                <h1 class="text-xl font-extrabold m-0">
                    {{ contactDisplayName }}
                </h1>
                <div v-if="formattedPhone !== contactDisplayName" class="text-slate-500">
                    {{ formattedPhone }}
                </div>
            </div>

            <div v-if="route.name === 'message-hub.contact-view'" class="mb-6">
                <h2 class="text-base font-extrabold mb-2">Contact Details</h2>
                <contact-view-content :editableRelationships="false" :expandAll="true" />
                <contact-attribute-sets-panel v-if="FEATURES.local_attributes" :expandAll="true" />
            </div>

            <div class="border-t border-solid border-slate-200 pt-4">
                <h2 class="text-base font-extrabold mb-4">Conversation</h2>

                <div v-if="!messageGroups.length" class="text-slate-400">No message history</div>

                <div class="flex flex-col-reverse gap-6">
                    <div v-for="group in messageGroups" :key="group.prettyDate" class="break-inside-avoid">
                        <div class="text-center text-xs text-slate-400 mb-2 font-semibold">
                            {{ group.prettyDate }}
                        </div>
                        <div class="flex flex-col gap-3">
                            <message-cloud
                                v-for="message in group.messages"
                                :key="message.id"
                                :message="message"
                                :unread="false"
                                :showDetails="true"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import ContactAttributeSetsPanel from '~/components/contacts/contact/ContactAttributeSetsPanel.vue';
import ContactViewContent from '~/components/contacts/contact/view/ContactViewContent.vue';
import { useConversation } from '~/composables/useConversation';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { displayContactName, formatPhoneNumber } from '~/components/filters';

const route = useRoute();
const FEATURES = inject('FEATURES');
const { messageGroups, activeThread } = useConversation();
const { contact } = useContactDetails();

const printing = ref(false);

const onBeforePrint = () => {
    printing.value = true;
};
const onAfterPrint = () => {
    printing.value = false;
};

const printStyleId = 'message-hub-print-styles';

const mountPrintStyles = () => {
    if (document.getElementById(printStyleId)) {
        return;
    }
    const style = document.createElement('style');
    style.id = printStyleId;
    style.textContent = [
        '@media print {',
        '  #spark-app { display: none !important; }',
        '  body { overflow: visible !important; height: auto !important; }',
        '  @page { margin: 0; }',
        '}',
    ].join('\n');
    document.head.appendChild(style);
};

const unmountPrintStyles = () => {
    document.getElementById(printStyleId)?.remove();
};

onMounted(() => {
    mountPrintStyles();
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
});

onUnmounted(() => {
    unmountPrintStyles();
    window.removeEventListener('beforeprint', onBeforePrint);
    window.removeEventListener('afterprint', onAfterPrint);
});

const contactDisplayName = computed(() => {
    if (activeThread.value) {
        return activeThread.value.contact_name || formatPhoneNumber(activeThread.value.contact_phone);
    }
    return displayContactName(contact.value);
});

const formattedPhone = computed(() => {
    const phone = activeThread.value?.contact_phone || contact.value.phone;
    return formatPhoneNumber(phone) || '-';
});
</script>
